/**
 * ReaderHeader - 閱讀器頂部導航
 */

import { Link } from 'react-router-dom';
import type { ThemeStyles } from './types';

export interface ReaderHeaderProps {
  novelId: string | undefined;
  novelTitle: string | null;
  chapterIndex: number;
  showUI: boolean;
  theme: 'light' | 'sepia' | 'dark';
  themeStyles: ThemeStyles;
  scrollProgress: number;
  // 事件處理
  onToggleTTS: () => void;
  onToggleSettings: () => void;
}

export function ReaderHeader({
  novelId,
  novelTitle,
  chapterIndex,
  showUI,
  theme,
  themeStyles,
  scrollProgress,
  onToggleTTS,
  onToggleSettings,
}: ReaderHeaderProps) {
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showUI ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      style={{
        backgroundColor: theme === 'dark' ? 'rgba(26,26,26,0.95)' : 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link
          to={novelId ? `/novels/${novelId}` : '/novels'}
          className="p-2 -ml-2 rounded-lg hover:bg-black/5 active:bg-black/10 transition-colors"
          style={{ color: themeStyles.color }}
          onClick={(e) => e.stopPropagation()}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </Link>

        <div className="text-center flex-1 mx-4">
          <p
            className="text-sm font-medium line-clamp-1"
            style={{ color: themeStyles.color, opacity: 0.8 }}
          >
            {novelTitle}
          </p>
          <p className="text-xs" style={{ color: themeStyles.color, opacity: 0.5 }}>
            第 {chapterIndex} 章
          </p>
        </div>

        <div className="flex items-center gap-1">
          {/* TTS 按鈕 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleTTS();
            }}
            className="p-2 rounded-lg hover:bg-black/5 active:bg-black/10 transition-colors"
            style={{ color: themeStyles.color }}
            title="語音朗讀"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            </svg>
          </button>

          {/* 設定按鈕 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSettings();
            }}
            className="p-2 -mr-2 rounded-lg hover:bg-black/5 active:bg-black/10 transition-colors"
            style={{ color: themeStyles.color }}
            aria-label="閱讀設定"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* 進度條 */}
      <div
        className="h-0.5 transition-all duration-300"
        style={{
          backgroundColor: theme === 'dark' ? '#333' : '#e5e7eb',
        }}
      >
        <div
          className="h-full bg-primary-500 transition-all duration-150"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>
    </header>
  );
}
